import React, { useEffect, useState, useCallback } from "react";
import BackButton from "../../../../BaseComponents/BackButton";
import BaseButton from "../../../../BaseComponents/BaseButton";
import { Details, SalaryAccess } from "../../../../Constant/HR/SalaryDetails";
import { SalaryDetaillist, UpdateSalaryAccess } from "../../../../Api/SalaryDetails";
import { centerIDPayload, checkStatusCodeSuccess } from "../../../../Constant/common";
import { toast } from "react-toastify";
import { holidayLabel } from "../../../../Constant/HR_HolidayTemplate/holidayTemplate";
import { useSelectedCenter } from "../../../CenterContext";
import { useNavigate } from "react-router-dom";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import {
  DollarSign,
  Users,
  UserX,
  User,
  Search,
  Check,
  ToggleLeft,
  ToggleRight,
} from "react-feather";

/* ── Access mode config ── */
const ACCESS_CFG = {
  [Details.ALLSTAFF]: {
    color: "#10b981",
    bg: "rgba(16,185,129,0.12)",
    border: "rgba(16,185,129,0.25)",
    Icon: Users,
    desc: "All staff can view their salary slips",
  },
  [Details.NOACCESSSTATUS]: {
    color: "#f43f5e",
    bg: "rgba(244,63,94,0.12)",
    border: "rgba(244,63,94,0.25)",
    Icon: UserX,
    desc: "No staff can view salary information",
  },
  [Details.SELECTEDSTAFF]: {
    color: "#6366f1",
    bg: "rgba(99,102,241,0.12)",
    border: "rgba(99,102,241,0.25)",
    Icon: User,
    desc: "Only selected staff can view their salary",
  },
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
const GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#10b981,#06b6d4)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#6366f1)",
];

const SalaryDetails = () => {
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [salaryList, setSalaryList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [handleswitch, setHandleswitch] = useState({});
  const [checks, setChecks] = useState([]);
  const [unselectedIds, setUnselectedIds] = useState([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();
  const navigate = useNavigate();
  const selectedCenter = selectedCenterPayload.center_id;

  /* ── Fetch ── */
  const fetchData = useCallback(() => {
    setLoading(true);
    SalaryDetaillist(selectedCenter ? `${centerIDPayload}${selectedCenter}` : `${centerIDPayload}`)
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) {
          const initialChecks = res.data.filter((s) => s.selected === 1).map((s) => s.id);
          const initialUnselected = res.data.filter((s) => s.selected !== 1).map((s) => s.id);
          setSalaryList(res.data);
          setChecks(initialChecks);
          setUnselectedIds(initialUnselected);
          if (initialChecks.length === res.data.length) setSelectedAccess(Details.ALLSTAFF);
          else if (initialChecks.length > 0) setSelectedAccess(Details.SELECTEDSTAFF);
          else setSelectedAccess(Details.NOACCESSSTATUS);
        } else {
          setSalaryList([]);
          toast.error(res.message);
        }
      })
      .catch(() => {
        setSalaryList([]);
        toast.error(Details.ERROROCCURED);
      })
      .finally(() => setLoading(false));
  }, [selectedCenter, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Access mode change ── */
  const handleAccessChange = (mode) => {
    setSelectedAccess(mode);
    if (mode === Details.ALLSTAFF) {
      const all = salaryList.map((s) => s.id);
      setChecks(all);
      setUnselectedIds([]);
    } else if (mode === Details.NOACCESSSTATUS) {
      const all = salaryList.map((s) => s.id);
      setChecks([]);
      setUnselectedIds(all);
    }
  };

  /* ── Individual checkbox ── */
  const handleCheckbox = (id) => {
    const isChecked = checks.includes(id);
    const newChecks = isChecked ? checks.filter((x) => x !== id) : [...checks, id];
    setChecks(newChecks);
    const newUnselected = salaryList
      .filter((s) => !newChecks.includes(s.id) && s.selected === 1)
      .map((s) => s.id);
    setUnselectedIds(newUnselected);
    if (newChecks.length === salaryList.length) setSelectedAccess(Details.ALLSTAFF);
    else if (newChecks.length > 0) setSelectedAccess(Details.SELECTEDSTAFF);
    else setSelectedAccess(Details.NOACCESSSTATUS);
  };

  const handleSelectAll = () => {
    const allOn = checks.length === salaryList.length;
    if (allOn) {
      setChecks([]);
      setUnselectedIds(salaryList.map((s) => s.id));
      setSelectedAccess(Details.NOACCESSSTATUS);
    } else {
      setChecks(salaryList.map((s) => s.id));
      setUnselectedIds([]);
      setSelectedAccess(Details.ALLSTAFF);
    }
  };

  const handleToggleSwitch = (id) => setHandleswitch((prev) => ({ ...prev, [id]: !prev[id] }));

  /* ── Save ── */
  const handleSave = () => {
    setBtnLoader(true);
    const salaryAccessPayload = salaryList.reduce((acc, staff) => {
      acc[staff.id] = !checks.includes(staff.id)
        ? "Disabled"
        : handleswitch[staff.id]
          ? "Current_cycle"
          : "Previous_cycle";
      return acc;
    }, {});
    UpdateSalaryAccess({
      accessType: selectedAccess,
      selectedStaffIds: checks,
      unselectedStaffIds: unselectedIds,
      salaryAccess: salaryAccessPayload,
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res.statusCode)) toast.success(res.message);
        else toast.error(res.message);
      })
      .catch(() => toast.error(Details.ERROROCCURED))
      .finally(() => setBtnLoader(false));
  };

  /* ── Filtered list ── */
  const filteredList = salaryList.filter(
    (s) =>
      !searchQuery ||
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.contact_no?.includes(searchQuery),
  );

  const allChecked = checks.length === salaryList.length && salaryList.length > 0;

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(16,185,129,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DollarSign size={20} strokeWidth={1.75} style={{ color: "#10b981" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {Details.SALARYACCESS}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {Details.STAFFAPP}
            </p>
          </div>
        </div>
        <BackButton onClick={() => navigate("/setting")} />
      </div>

      {/* Access mode selector */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        <div style={{ height: 3, background: "linear-gradient(90deg,#10b981,#6366f1)" }} />
        <div style={{ padding: "20px 24px" }}>
          <h2
            style={{
              margin: "0 0 14px",
              fontSize: "0.9375rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {Details.SALARYDETAILS}
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 10,
            }}
          >
            {SalaryAccess.map((opt) => {
              const cfg = ACCESS_CFG[opt.value] || {};
              const { Icon } = cfg;
              const active = selectedAccess === opt.value;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleAccessChange(opt.value)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 12,
                    textAlign: "left",
                    border: `1.5px solid ${active ? cfg.color : "var(--glass-border)"}`,
                    background: active ? cfg.bg : "var(--icon-btn-bg)",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    boxShadow: active ? `0 4px 16px ${cfg.color}25` : "none",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: active ? `${cfg.color}20` : "var(--glass-border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {Icon && (
                      <Icon
                        size={15}
                        strokeWidth={2}
                        style={{ color: active ? cfg.color : "var(--text-muted)" }}
                      />
                    )}
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        color: active ? cfg.color : "var(--text-primary)",
                      }}
                    >
                      {opt.label}
                    </div>
                    <div
                      style={{
                        fontSize: "0.6875rem",
                        color: "var(--text-muted)",
                        marginTop: 2,
                        lineHeight: 1.4,
                      }}
                    >
                      {cfg.desc}
                    </div>
                  </div>
                  {active && (
                    <div
                      style={{
                        marginLeft: "auto",
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: cfg.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Check size={11} strokeWidth={3} color="#fff" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Staff list */}
      <div
        className="animate-slide-up"
        style={{
          animationDelay: "60ms",
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--glass-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={handleSelectAll}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 12px",
                borderRadius: 8,
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
                border: `1px solid ${allChecked ? "rgba(99,102,241,0.3)" : "var(--glass-border)"}`,
                background: allChecked ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
                color: allChecked ? "var(--brand-indigo)" : "var(--text-secondary)",
              }}
            >
              {allChecked && <Check size={12} strokeWidth={3} />}
              {Details.SELECTALL}
            </button>
            <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
              {checks.length}/{salaryList.length} selected
            </span>
          </div>
          {/* Search */}
          <div style={{ position: "relative", minWidth: 200 }}>
            <Search
              size={13}
              strokeWidth={2}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-muted)",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder={Details.SEARCH}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              ref={(el) => {
                if (el) el.style.setProperty("padding-left", "30px", "important");
              }}
              style={{
                width: "100%",
                padding: "7px 10px",
                borderRadius: 8,
                border: "1px solid var(--glass-border)",
                background: "var(--icon-btn-bg)",
                color: "var(--text-primary)",
                fontSize: "0.8125rem",
                outline: "none",
              }}
            />
          </div>
        </div>

        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          {filteredList.map((staff, i) => {
            const isChecked = checks.includes(staff.id);
            const gradient = GRADIENTS[(staff.name?.charCodeAt(0) || i) % GRADIENTS.length];
            return (
              <div
                key={staff.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 20px",
                  borderBottom:
                    i < filteredList.length - 1 ? "1px solid var(--glass-border)" : "none",
                  background: isChecked ? "rgba(99,102,241,0.04)" : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isChecked) e.currentTarget.style.background = "var(--nav-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isChecked
                    ? "rgba(99,102,241,0.04)"
                    : "transparent";
                }}
              >
                {/* Checkbox */}
                <button
                  type="button"
                  onClick={() => handleCheckbox(staff.id)}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    flexShrink: 0,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    background: isChecked ? "var(--brand-indigo)" : "transparent",
                    border: `1.5px solid ${isChecked ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: isChecked ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
                  }}
                >
                  {isChecked && <Check size={11} strokeWidth={3} color="#fff" />}
                </button>

                {/* Avatar */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: gradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#fff" }}>
                    {getInitials(staff.name)}
                  </span>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--text-primary)" }}
                  >
                    {staff.name}
                  </div>
                  {staff.contact_no && (
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {staff.contact_no}
                    </div>
                  )}
                </div>

                {/* Current cycle toggle — only when checked */}
                {isChecked && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {Details.CURRENTSALARY}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleToggleSwitch(staff.id)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        color: handleswitch[staff.id] ? "#10b981" : "var(--text-muted)",
                      }}
                    >
                      {handleswitch[staff.id] ? (
                        <ToggleRight size={22} strokeWidth={2} style={{ color: "#10b981" }} />
                      ) : (
                        <ToggleLeft
                          size={22}
                          strokeWidth={2}
                          style={{ color: "var(--text-muted)" }}
                        />
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Save */}
      <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 8 }}>
        <BaseButton
          color="primary"
          onClick={handleSave}
          loader={btnLoader}
          disabled={btnLoader || selectedAccess === null}
        >
          {holidayLabel.Save}
        </BaseButton>
      </div>
    </div>
  );
};

export default SalaryDetails;
